import { t } from "../../../i18n/i18n";

/**
 * `complete` / `in_progress` (`current` before) / `stopped` / anything else →
 * the four dot styles.
 */
const STAGE_CLASS = {
  complete: "is-done",
  in_progress: "is-current",
  current: "is-current",
  stopped: "is-stopped",
};

/**
 * The horizontal stage strip. Lives here rather than in Payout.jsx because the
 * journey card and the history table's expanded row draw the same thing — they
 * differ only in what each stage prints underneath, hence `meta`.
 */
export default function JourneyTrack({ stages, meta }) {
  if (!stages.length) return null;

  /** The green line stops at the furthest dot reached — current, else the last complete one. */
  const current = stages.findIndex((stage) => STAGE_CLASS[stage.status] === "is-current");
  const reached =
    current >= 0
      ? current
      : stages.reduce((last, stage, i) => (stage.status === "complete" ? i : last), -1);

  /** A stopped journey gets a red run from that last cleared dot to where it halted. */
  const stopped = stages.findIndex((stage) => stage.status === "stopped");

  /** Dot centres: the two end steps hug the track, the rest sit mid-column. */
  const centre = (i) =>
    i === 0
      ? "10px"
      : i === stages.length - 1
        ? "calc(100% - 10px)"
        : `${((i + 0.5) / stages.length) * 100}%`;

  return (
    <div className="journey-track">
      <span className="journey-line" />
      <span
        className="journey-line-done"
        style={{ right: reached >= 0 ? `calc(100% - ${centre(reached)})` : "100%" }}
      />
      {stopped >= 0 && (
        <span
          className="journey-line-stopped"
          style={{
            left: reached >= 0 ? centre(reached) : "10px",
            right: `calc(100% - ${centre(stopped)})`,
          }}
        />
      )}

      <div
        className="journey-steps"
        // The count, not the columns: an inline `grid-template-columns` beat the
        // phone rule that turns the strip into a list (Payout.scss).
        style={{ "--journey-steps": stages.length }}
      >
        {stages.map((stage) => (
          <div
            key={stage.key}
            className={`journey-step ${STAGE_CLASS[stage.status] ?? "is-pending"}`}
          >
            <span className="journey-dot">
              {stage.status === "complete" && (
                <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4">
                  <path d="m3 6.2 2 2L9 4" />
                </svg>
              )}
              {stage.status === "stopped" && (
                <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4">
                  <path d="m3.6 3.6 4.8 4.8M8.4 3.6 3.6 8.4" strokeLinecap="round" />
                </svg>
              )}
            </span>
            <span className="journey-step-title">{t(stage.label)}</span>
            <span
              className={`journey-step-meta${STAGE_CLASS[stage.status] === "is-current" ? " is-active" : ""}`}
            >
              {meta(stage)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
